/**
 * use-accounts — reactive view over the web multi-account registry + the
 * switch/add/remove orchestration used by the account switcher UI.
 *
 * Switching an account is data-preserving: we set the active pointer, re-prime
 * the session actor for the new identity and let the PowerSync provider swap to
 * that user's DB instance (it reacts to the active user id). We NEVER call
 * `disconnectAndClear` on a switch — that would destroy the data. Only an
 * explicit "Sign out" / "Remove account" wipes a database.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import { type AccountsState, getActiveAccount, type StoredAccount } from "@agape/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  activeTabAccountId,
  clearTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accounts" || e.key === null) {
        reload();
      }
    };
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  const active = (): StoredAccount | null => resolveTabAccount(state(), activeTabAccountId());
  const activeUserId = () => active()?.userId ?? null;

  /**
   * Switches THIS TAB to a known account.
   * Preserves current section route; if on an entity route with parameters ($),
   * falls back to the parent section (or "/" if no ancestor).
   * Order (§7.0b): clear cache & re-prime BEFORE writing tab account id.
   */
  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);

    // D1 & D2: Preserve section route; fall back to section if on entity route ($)
    const matches = router?.state?.matches;
    const matchWithParam = matches?.findLast((m: { fullPath?: string }) =>
      Boolean(m.fullPath && m.fullPath.includes("$")),
    );
    if (matchWithParam?.fullPath) {
      const dollarIdx = matchWithParam.fullPath.indexOf("$");
      const prefix = matchWithParam.fullPath.slice(0, dollarIdx).replace(/\/+$/, "");
      const target = prefix.length > 0 ? prefix : "/";
      await (navigate as (opts: { to: string }) => Promise<void>)({ to: target });
    }
  };

  /**
   * Switches THIS TAB to a known account without navigating (used by chooser).
   * Order (§7.0b): clear cache & re-prime BEFORE writing tab account id.
   */
  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    queryClient.clear();
    primeSessionForAccount(guest);
    if (guest) {
      writeTabAccountId(guest.userId);
    }
    // D5: Continuing as guest crosses a privilege boundary; safe landing at root is deliberate
    await navigate({ to: "/" });
  };

  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeUserId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        writeTabAccountId(fallback.userId);
        await navigate({ to: "/" });
      } else {
        clearTabAccountId();
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
